import { IScope, IRootScopeService, IController } from "angular";
import { chain, get, reduce, has } from "lodash";
import * as moment from "moment-timezone";
import { last, pullAt } from "lodash";

import { PromotionModel, UserModel } from "@/models";
import { PromotionPolicy } from "@/policies";
import {
  iPromotion,
  iPromotionTier,
  iPromotionType,
} from "@/interfaces/promotions";
import { auth, notifications } from "@/services";
import { ActionCancelledException } from "@/exceptions";
import { iFileDropOptions, iFileDropShapes } from "@/interfaces/files";
import { iSiteSettings } from "@/interfaces/siteSettings";
import { IUploadsApiResource } from "@/angular/services/api/uploads";
import {
  IUpdatableResourceClass,
  IResource,
} from "@/interfaces/angular/resource";
import { ICategoryTypesApiResource } from "@/angular/services/api/categoryTypes";
import { ICategoriesApiResource } from "@/angular/services/api/categories";
import { iCategoryType, iCategory } from "@/interfaces/categories";

const fileOptionsCommon = {
  resize: true,
  resizeShape: iFileDropShapes.rectangle,
};

class LoadingStack {
  /**
   * @param stack A stacked array of Loading.
   */
  public constructor(protected readonly stack: Loading[] = []) { }

  /**
   * Returns the head of the loading stack.
   */
  public head(): Loading | null {
    if (this.stack.length === 0) {
      return null;
    }

    return last(this.stack);
  }

  /**
   * Pull a label from the stack.
   * @param label The label to pull.
   */
  public pull(label: string): LoadingStack {
    const index = this.findIndex(label);

    if (index === null) {
      return this;
    }

    const stack = [...this.stack];
    pullAt(stack, index);

    return new LoadingStack(stack);
  }

  /**
   * Push a label, and optionally some progress, onto the stack.
   * @param label The label to push.
   * @param progress The progress to push.
   */
  public push(label: string, progress: number = 0): LoadingStack {
    const index = this.findIndex(label);
    const loading = new Loading(label, progress);

    if (index === null) {
      return new LoadingStack([...this.stack, loading]);
    }

    return new LoadingStack(
      Object.assign([], this.stack, { [index]: loading }),
    );
  }

  /**
   * Finds the index of the given label in the stack.
   * @param label The label to find
   */
  protected findIndex(label: string): number | null {
    const index = this.stack.findIndex((loading) => {
      return loading.label === label;
    });

    if (index < 0) {
      return null;
    }

    return index;
  }
}

class Loading {
  /**
   * @param label The loading label.
   * @param progress The loading progress.
   */
  public constructor(
    public readonly label: string,
    public readonly progress: number = 0,
  ) { }
}

interface IFile {
  uploaded: boolean;
  data: string;
}

class Controller implements IController {
  static $inject = [
    "$scope",
    "$rootScope",
    "$uibModalInstance",
    "CategoryTypesApi",
    "CategoriesApi",
    "confirmDialog",
    "promotionId",
    "PromotionsApi",
    "UploadsApi",
    "Helpers",
    "thisSiteSettings",
    "Users",
  ];

  public close: (reason: string) => void;
  public categories: iCategory[];
  public categoryTypes: iCategoryType[] = [];
  public errors: object = {};
  public file: IFile = {
    data: null,
    uploaded: false,
  };
  public fileOptionsGold: iFileDropOptions = {
    ...fileOptionsCommon,
    resizeSize: { w: 1250, h: 320 },
    resizeAspectRatio: 0,
  };
  public fileOptionsPlatinum: iFileDropOptions = {
    ...fileOptionsCommon,
    resizeSize: { w: 1250, h: 640 },
    resizeAspectRatio: 0,
  };
  public promotion: iPromotion;
  public statuses: object[] = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];
  public tiers: object[] = [];
  public types: object[] = [
    { label: "Offer", value: "offer" },
    { label: "Job", value: "job" },
    { label: "Advert", value: "advert" },
    { label: "Event", value: "event" },
  ];

  protected forceClose: boolean = false;
  protected loading: LoadingStack = new LoadingStack();
  protected user: UserModel | null = null;

  public constructor(
    protected $scope: IScope,
    protected $rootScope: IRootScopeService,
    protected $uibModalInstance: any,
    protected categoryTypesApi: ICategoryTypesApiResource,
    protected categoriesApi: ICategoriesApiResource,
    protected confirmDialog: (string) => Promise<Boolean>,
    protected promotionId: number | null,
    protected PromotionsApi: IUpdatableResourceClass<IResource<iPromotion>>,
    protected UploadsApi: IUploadsApiResource,
    protected Helpers: any,
    protected thisSiteSettings: iSiteSettings,
    protected Users: any,
  ) {
    this.user = auth.user();

    this.busyDoing = this.busyDoing.bind(this);
    this.busyProgress = this.busyProgress.bind(this);
    this.canCreate = this.canCreate.bind(this);
    this.canManage = this.canManage.bind(this);
    this.canManageStatus = this.canManageStatus.bind(this);
    this.fetchCategories = this.fetchCategories.bind(this);
    this.fetchCategoryTypes = this.fetchCategoryTypes.bind(this);
    this.hasImage = this.hasImage.bind(this);
    this.isBusy = this.isBusy.bind(this);
    this.isNew = this.isNew.bind(this);
    this.onBodyChange = this.onBodyChange.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onFileSelect = this.onFileSelect.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.setCategories = this.setCategories.bind(this);
    this.submitLabel = this.submitLabel.bind(this);
  }

  /**
   * Called after the components controller has initialized and all bindings are available.
   */
  public async $onInit(): Promise<void> {
    const loading = "Preparing interface...";
    this.busy(loading);

    await this.setPromotionId(this.promotionId);
    await this.fetchCategoryTypes();
    this.tiers = chain(this.thisSiteSettings.promotion_tiers)
      .map((tier, key) => ({ key, ...tier }))
      .filter((tier) => tier.enabled)
      .orderBy("level", "desc")
      .value();

    this.close = this.$uibModalInstance.close;

    this.finish(loading);
    this.$scope.$apply();
  }

  /**
   * Returns the doing label.
   */
  public busyDoing(): string {
    if (!this.isBusy()) {
      return "";
    }

    return this.loading.head().label;
  }

  /**
   * Returns the progress number.
   */
  public busyProgress(): number {
    if (!this.isBusy()) {
      return 0;
    }

    return this.loading.head().progress;
  }

  /**
   * Can the user create promotions.
   */
  public canCreate(): boolean {
    return PromotionPolicy.canCreate(this.user);
  }

  /**
   * Can the user manage promotions.
   */
  public canManage(): boolean {
    if (!this.promotion) {
      return false;
    }

    return PromotionPolicy.canManage(
      this.user,
      new PromotionModel(this.promotion),
    );
  }

  /**
   * Can the user manage promotion statuses.
   */
  public canManageStatus(): boolean {
    return PromotionPolicy.canManageStatus(this.user);
  }

  /**
   * Returns whether the promotion has an image.
   */
  public hasImage(): boolean {
    if (!this.promotion) {
      return false;
    }

    switch (this.promotion.tier) {
      case iPromotionTier.gold:
      case iPromotionTier.platinum:
        return true;

      default:
        return false;
    }
  }

  /**
   * Returns whether the directive is busy.
   */
  public isBusy(): boolean {
    return this.loading.head() !== null;
  }

  /**
   * Returns whether the promotion is new.
   */
  public isNew(): boolean {
    return this.promotionId === null;
  }

  /**
   * Handles body content changes.
   * @param content The content
   */
  public onBodyChange(content): void {
    this.promotion.body = content;
  }

  /**
   * Handles deleting a promotion.
   */
  public async onDelete(): Promise<void> {
    const confirm = await this.confirmDialog(
      "Are you sure you want to delete this promotion?",
    );
    const loading = "Deleting...";

    if (!confirm) {
      return Promise.resolve();
    }

    this.busy(loading);

    this.PromotionsApi.delete({ id: this.promotion.id })
      .$promise.then((response: any) => {
        response.data = this.promotion;
        response.deleted = true;
        this.$uibModalInstance.close(response);
        window.location.reload();
      })
      .finally(() => this.finish(loading));
  }

  /**
   * Handles file selection.
   * @param file The select file data.
   */
  public onFileSelect(file): void {
    this.file.data = file;
    this.file.uploaded = false;
  }

  /**
   * Handles form submissions.
   */
  public async onSubmit(): Promise<void> {
    try {
      if (this.file.data !== null && !this.file.uploaded) {
        await this.upload(this.file, this.promotion);
      }

      this.promotion = await this.save(this.promotion);

      notifications.display({
        message: this.canManage()
          ? ""
          : "Your promotion has been submitted for approval",
        theme: "success",
        title: "Promotion saved",
      });

      this.$uibModalInstance.close(this.promotion);
    } catch (err) {
      let message = err.toString();

      // Action was cancelled so do nothing.
      if (err instanceof ActionCancelledException) {
        return;
      }

      // Handle error messages
      else if (has(err, "data.message")) {
        message = get(err, "data.message");
      }

      // Handle validation errors
      else if (err.status === 422) {
        message = reduce(
          err.data,
          (carry, messages) => {
            return carry.concat(messages);
          },
          [],
        ).join(" ");
      }

      // Display errors
      notifications.display({
        message,
        theme: "error",
        title: "Error saving promotion",
      });
    }
  }

  /**
   * Replaces the selected categories.
   *
   * @param {number[]} categoryIds
   * @returns {void}
   */
  public setCategories(categoryIds: number[]): void {
    this.promotion.categories = categoryIds;
    this.$scope.$apply();
  }

  /**
   * Gets the submit label.
   */
  public submitLabel(): string {
    if (!this.isNew()) {
      return "Update";
    }

    if (
      !PromotionPolicy.createPaymentRequired(this.user, this.promotion.tier)
    ) {
      return "Save";
    }

    const tier = this.thisSiteSettings.promotion_tiers[this.promotion.tier];
    return `Purchase for ${this.thisSiteSettings.currency_symbol}${tier.price}`;
  }

  /**
   * Toggles the directives busy state with the given label.
   * @param label The message to display.
   * @param progress The progress.
   */
  protected busy(label: string, progress: number = 0): void {
    this.loading = this.loading.push(label, progress);
  }

  /**
   * Fetches a promotion by the given ID.
   * @param id
   */
  protected fetch(id: number): Promise<PromotionModel> {
    const loading = "Loading promotion...";
    this.busy(loading);

    return new Promise((resolve, reject) => {
      this.PromotionsApi.get({ id })
        .$promise.then((promotion) => {
          // This is really annoying, I don't know what this type is,
          // it's NOT an iPromotion though, and I can't find it's shape
          // in the resource type definition....
          resolve(new PromotionModel((promotion as any).data));
        })
        .catch((err) => reject(err))
        .finally(() => this.finish(loading));
    });
  }

  /**
   * Retrieves categories from the API
   *
   * @returns {Promise<void>}
   */
  protected fetchCategories(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.categoriesApi.query((response) => {
        this.categories = response;
        // Insert each category into its parent type
        this.categoryTypes.forEach((categoryType) => {
          const cats = this.categories.filter((cat) => {
            return cat.type_id === categoryType.id;
          });

          categoryType.categories = cats;
        });
        resolve();
      }, reject);
    });
  }

  /**
   * Retrieves category types from the API
   *
   * @returns {Promise<void>}
   */
  protected fetchCategoryTypes(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.categoryTypesApi.query(
        { "categorizables[]": ["promotion"] },
        async (response: any) => {
          this.categoryTypes = response;
          await this.fetchCategories();
          resolve();
        },
        reject,
      );
    });
  }

  /**
   * Sets loading to null.
   * @param label The label to finish.
   */
  protected finish(label: string): void {
    this.loading = this.loading.pull(label);
  }

  /**
   * Maybe returns a stripe ID for the given promotion.
   *
   * @param promotion The promotion to get a stripe ID for.
   */
  protected async maybeGetStripeId(
    promotion: iPromotion,
  ): Promise<null | string> {
    return new Promise((resolve, reject) => {
      // If we have a promotion ID
      if (promotion.id > 0) {
        resolve(null);
        return;
      }

      // If we don't need to make a payment
      if (!PromotionPolicy.createPaymentRequired(this.user, promotion.tier)) {
        resolve(null);
        return;
      }

      this.Users.manageCard()
        .result.then((card) => {
          if (!card) {
            throw new ActionCancelledException(
              "User cancelled the card modal.",
            );
          }

          resolve(card.stripe_id || null);
        })
        .catch(reject);
    });
  }

  /**
   * Saves the given promotion.
   * @param data The promotion to save.
   */
  protected async save(data: iPromotion): Promise<iPromotion> {
    const loading = "Saving...";
    this.busy(loading);

    return new Promise(async (resolve, reject) => {
      const { id, created_at, updated_at, ...rest } = data;
      let request: IResource<iPromotion>;
      const promotion: iPromotion = {
        ...rest,
        ends_at: window.moment
          .tz(data.ends_at, "Europe/London")
          .tz("UTC")
          .format(this.Helpers.isoFormat),
        starts_at: window.moment
          .tz(data.starts_at, "Europe/London")
          .tz("UTC")
          .format(this.Helpers.isoFormat),
        stripe_id: await this.maybeGetStripeId(data),
      } as iPromotion;

      if (id > 0) {
        promotion.id = id;
        request = this.PromotionsApi.update(promotion);
      } else {
        request = this.PromotionsApi.save(promotion);
      }

      request
        .suppressValidationAlerts()
        .$promise.then(resolve)
        .catch(reject)
        .finally(() => this.finish(loading));
    });
  }

  /**
   * Sets the promotion ID.
   * @param promotionId The promotion ID to set.
   */
  protected async setPromotionId(
    promotionId: number | null,
  ): Promise<iPromotion> {
    this.promotionId = promotionId;

    if (promotionId === null) {
      this.promotion = {
        body: "",
        categories: [],
        created_at: "",
        ends_at: window
          .moment()
          .add(1, "months")
          .minute(0)
          .format(this.Helpers.isoFormat),
        excerpt: "",
        id: 0,
        img: "",
        is_system: 0,
        offline_payment: null,
        price_paid: null,
        slug: "",
        starts_at: window.moment().minute(0).format(this.Helpers.isoFormat),
        status: "pending",
        stripe_id: null,
        tell_me_more_logs: [],
        title: "",
        total_impressions: 0,
        updated_at: "",
        upload_files: {
          images: [],
          documents: [],
          videos: [],
          audio: [],
        },
        uploads: [],
        user_id: this.user.get("id") as number,
        settings: {
          company_name: "",
          promotion_type: iPromotionType.offer,
        },
        tier: iPromotionTier.gold,
        total_claimed: 0,
        type: "promotion",
      };


      return Promise.resolve(this.promotion);
    }

    try {
      const promotion = await this.fetch(promotionId);
      promotion.data.starts_at = new Date(promotion.data.starts_at);
      promotion.data.ends_at = new Date(promotion.data.ends_at);

      return await Promise.resolve((this.promotion = promotion.data));
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Uploads the given file for the given promotion.
   * @param file The file to upload.
   * @param promotion The promotion to attach the image to.
   */
  protected upload(file: IFile, promotion: iPromotion) {
    return new Promise<void>((resolve, reject) => {
      if (file.uploaded) {
        return resolve();
      }

      const loading = "Uploading image...";

      this.busy(loading);

      this.UploadsApi.uploadFile(file.data, "post", false)
        .then(
          (upload) => {
            file.uploaded = true;
            promotion.img = upload.path;
            resolve();
          },
          reject,
          (progress: number) => {
            this.busy(loading, progress);
          },
        )
        .finally(() => this.finish(loading));
    });
  }
}

angular.module("app").controller("managePromotionController", Controller);
